import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockIcon,
  Print as PrintIcon
} from '@mui/icons-material';

const ModalDetalleComparacion = ({ open, onClose, cierre, detalleComparacion, loading }) => {
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getDiferenciaColor = (diferencia) => {
    if (diferencia === 0) return 'success';
    if (diferencia > 0) return 'warning';
    return 'error';
  };

  const getDiferenciaLabel = (diferencia) => {
    if (diferencia === 0) return 'Cuadra Perfecto';
    if (diferencia > 0) return 'Sobrante';
    return 'Faltante';
  };

  // Determinar si el cierre está cerrado
  const esCierreCerrado = () => {
    const fechaCierre = detalleComparacion?.arqueo?.fechaCierre || cierre?.fechaCierre;
    if (!fechaCierre) return false;
    const fecha = new Date(fechaCierre);
    return fecha.getFullYear() > 1900;
  };

  if (!cierre) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ 
        backgroundColor: '#1976d2', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <LockIcon />
        {esCierreCerrado() ? 'Cerrar Caja - Arqueo Final' : 'Cierre de Caja - Vista Previa (Sin Cerrar)'}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detalleComparacion ? (
          <>
            {/* Encabezado con información del cierre */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Usuario:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {detalleComparacion.arqueo?.colaborador || cierre.colaborador}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fecha y Hora:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(detalleComparacion.arqueo?.fechaCierre || cierre.fechaCierre)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Sucursal:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {detalleComparacion.arqueo?.sucursal || cierre.sucursal}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Supervisor Autorizó:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {detalleComparacion.arqueo?.supervisor || cierre.supervisor || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Título de la tabla */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              {esCierreCerrado() ? 'Cierre de Caja - Comparación de Valores' : 'Valores del Sistema - Caja Abierta'}
            </Typography>

            {/* Alerta si no está cerrado */}
            {!esCierreCerrado() && (
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                <Typography variant="body2" color="text.primary">
                  <strong>⚠️ Nota:</strong> Esta caja aún no ha sido cerrada. Los valores mostrados corresponden únicamente al sistema.
                  Para ver la comparación con los valores del cajero, el cierre debe completarse.
                </Typography>
              </Paper>
            )}

            {/* Tabla de comparación */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Valor Sistema
                    </TableCell>
                    {esCierreCerrado() && (
                      <>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Valor Cajero
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Diferencia
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalleComparacion.comparacion && detalleComparacion.comparacion.map((item, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: item.descripcion.includes('EFECTIVO') ? '#fff9e6' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <strong>{item.descripcion}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(item.valorSistema)}
                      </TableCell>
                      {esCierreCerrado() && (
                        <>
                          <TableCell align="right">
                            {formatNumber(item.valorCajero)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatNumber(item.diferencia)}
                              color={getDiferenciaColor(item.diferencia)}
                              size="small"
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}

                  {/* Fila de totales */}
                  {detalleComparacion.totales && (
                    <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                      <TableCell>
                        <strong>TOTALES GENERALES</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(detalleComparacion.totales.sistema)}</strong>
                      </TableCell>
                      {esCierreCerrado() && (
                        <>
                          <TableCell align="right">
                            <strong>{formatNumber(detalleComparacion.totales.cajero)}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatNumber(detalleComparacion.totales.diferencia)}
                              color={getDiferenciaColor(detalleComparacion.totales.diferencia)}
                              size="medium"
                              sx={{ minWidth: 100, fontWeight: 'bold' }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Resumen de diferencias */}
            {detalleComparacion.totales && (
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Resumen General:</strong>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={esCierreCerrado() ? 4 : 12}>
                    <Typography variant="body2" color="text.secondary">
                      Total Sistema:
                    </Typography>
                    <Typography variant="h6">
                      {formatNumber(detalleComparacion.totales.sistema)}
                    </Typography>
                  </Grid>
                  {esCierreCerrado() && (
                    <>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total Cajero:
                        </Typography>
                        <Typography variant="h6">
                          {formatNumber(detalleComparacion.totales.cajero)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Diferencia Neta:
                        </Typography>
                        <Typography 
                          variant="h6" 
                          color={
                            detalleComparacion.totales.diferencia === 0 
                              ? 'success.main' 
                              : detalleComparacion.totales.diferencia > 0 
                                ? 'warning.main' 
                                : 'error.main'
                          }
                        >
                          {formatNumber(detalleComparacion.totales.diferencia)}
                          {' '}
                          ({getDiferenciaLabel(detalleComparacion.totales.diferencia)})
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Denominaciones si existen */}
            {detalleComparacion.denominaciones && detalleComparacion.denominaciones.detalle && 
             detalleComparacion.denominaciones.detalle.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Conteo de Denominaciones
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Denominación</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Cant. Apertura
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Cant. Cierre
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Total Apertura
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Total Cierre
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalleComparacion.denominaciones.detalle.map((denom, index) => (
                        <TableRow key={index}>
                          <TableCell>{formatNumber(denom.valorDenominacion)}</TableCell>
                          <TableCell>{denom.tipoDenominacion}</TableCell>
                          <TableCell align="right">{denom.cantidadApertura || 0}</TableCell>
                          <TableCell align="right">{denom.cantidadCierre || 0}</TableCell>
                          <TableCell align="right">
                            {formatNumber(denom.totalApertura)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(denom.totalCierre)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell colSpan={4}>
                          <strong>TOTALES</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatNumber(detalleComparacion.denominaciones.totalApertura)}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatNumber(detalleComparacion.denominaciones.totalCierre)}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Información adicional */}
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valor Inicio de Caja:</strong>
                </Typography>
                <Typography variant="body1">
                  {formatNumber(detalleComparacion.arqueo?.valorInicio || cierre.valorInicio)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valor Cierre de Caja:</strong>
                </Typography>
                <Typography variant="body1">
                  {formatNumber(detalleComparacion.arqueo?.valorCierre || cierre.valorCierre)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Comisión Aplicada:</strong>
                </Typography>
                <Typography variant="body1">
                  {formatNumber(detalleComparacion.arqueo?.comision || cierre.comision)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Comentarios del Cierre:</strong>
                </Typography>
                <Typography variant="body1">
                  {detalleComparacion.arqueo?.comentarios || cierre.comentarios || 'Sin comentarios'}
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No se pudo cargar el detalle del cierre
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          disabled={loading}
        >
          Imprimir
        </Button>
        <Button
          startIcon={<CloseIcon />}
          onClick={onClose}
          variant="contained"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDetalleComparacion;
